import * as R from "ramda";
import { sortBy } from "../language/module";

export type ProductHydrationErrors = {
  entitlementTypeId?: string | null;
  fulfillmentSeasonId?: string | null;
  ageBin?: string;
  errorCount: number;
}

/**
 * One group from GET /getproducthydrationerrors. An older API build sends one entry per error instead of errorCount.
 */
export type ProductHydrationErrorsResponse = Omit<ProductHydrationErrors, 'errorCount'> & {
  errorCount?: number | null;
  errors?: unknown[] | null;
}

/**
 * Reduces a response group to the count the page shows. Drop the errors fallback once every environment's API
 * sends errorCount.
 */
export const toProductHydrationErrors = (
  { errorCount, errors, ...key }: ProductHydrationErrorsResponse,
): ProductHydrationErrors => ({
  ...key,
  errorCount: errorCount ?? errors?.length ?? 0,
});

export type RetryHydrationErrorsRequest = {
  resortAbbrev: string;
  entitlementTypeId: number | null;
  fulfillmentSeasonId?: number | null;
  ageBin?: string;
}

export const getRetryRequestIdFromRequest = (request: RetryHydrationErrorsRequest) : string => {
  return `${request.resortAbbrev}_${request.entitlementTypeId}_${request.fulfillmentSeasonId}_${request.ageBin}`;
}

const toRetryId = (value?: string | null): number | null => {
  const parsed = parseInt(value ?? '');
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Builds the retry request for a hydration-error row. The registry key is derived from this request on both
 * sides — reducer and reader — so both must go through here or their keys drift (see TECHDEBT 2026-08-31).
 */
export const makeRetryRequest = (
  resortAbbrev: string,
  hydrationErrors?: ProductHydrationErrors,
): RetryHydrationErrorsRequest | undefined => hydrationErrors && ({
  resortAbbrev:        resortAbbrev,
  entitlementTypeId:   toRetryId(hydrationErrors.entitlementTypeId),
  fulfillmentSeasonId: toRetryId(hydrationErrors.fulfillmentSeasonId),
  ageBin:              hydrationErrors.ageBin,
});

const defaultHydrationErrors = (entitlementTypeId?: string, fulfillmentSeasonId?: string, ageBin?: string) : ProductHydrationErrors => {
  return {
    entitlementTypeId: entitlementTypeId,
    fulfillmentSeasonId: fulfillmentSeasonId,
    ageBin: ageBin,
    errorCount: 0,
  };
}

export const getProductHydrationErrors = (
  hydrationErrors: ProductHydrationErrors[],
  entitlementTypeId?: string,
  fulfillmentSeasonId?: string,
  ageBin?: string
): ProductHydrationErrors => {
  return hydrationErrors?.length
    ? hydrationErrors.find((phe) => {
        return (
          phe.entitlementTypeId === entitlementTypeId &&
          phe.fulfillmentSeasonId?.toString() === fulfillmentSeasonId?.toString() &&
          phe.ageBin === ageBin
        );
      }) ??
        defaultHydrationErrors(entitlementTypeId, fulfillmentSeasonId, ageBin)
    : defaultHydrationErrors(entitlementTypeId, fulfillmentSeasonId, ageBin);
};
    
export const sortByHydrationErrorsProp = R.curry((sortAsc: boolean, values: any[]) => {
  return sortBy(
    (element) =>
      element["hydrationErrors"]
        ? (element["hydrationErrors"] as ProductHydrationErrors).errorCount
        : 0,
    sortAsc,
    values
  );
});
